import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Button, Container, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import React, { useRef } from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { useHeroOrbitModelMotion } from "#/features/homePage/hooks/useHeroOrbitModelMotion";
import { useLandingDecor3dMobileEntrance } from "#/features/homePage/hooks/useLandingDecor3dMobileEntrance";
import { LANDING_DECOR_BRAND_BASE_AZIMUTH, LANDING_DECOR_BRAND_BASE_POLAR, LANDING_DECOR_CANVAS_SIZE_PX, LANDING_DECOR_GLASS_OPACITY_SX, LANDING_DECOR_MODEL_CAMERA, LANDING_DECOR_MODEL_DISTANCE, LANDING_DECOR_MODEL_FOV, LANDING_DECOR_MODEL_TARGET } from "#/features/homePage/lib/landingDecor3dConstants";
import { LANDING_PRIMARY_PLAYGROUND_HREF } from "#/features/homePage/lib/landingPlaygroundDemos";
import { HomeLandingHeroPreview } from "#/features/homePage/ui/landing/HomeLandingHeroPreview";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { LogoModelView } from "#/shared/ui/molecules/LogoModelView";
type HomeLandingHeroProps = {
  LL: TranslationFunctions;
  pageScrollViewport: HTMLDivElement | null;
};
const AmbientBackground = () => {
  const $ = _c(8);
  const theme = useTheme();
  let t0;
  if ($[0] !== theme.appDesign.accent) {
    t0 = alpha(theme.appDesign.accent, 0.16);
    $[0] = theme.appDesign.accent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== theme.appDesign.accentSoft) {
    t1 = alpha(theme.appDesign.accentSoft, 0.12);
    $[2] = theme.appDesign.accentSoft;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== theme.appDesign.surfaceLow) {
    t2 = alpha(theme.appDesign.surfaceLow, 0.12);
    $[4] = theme.appDesign.surfaceLow;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `
          radial-gradient(circle at 18% 18%, ${t0} 0%, transparent 42%),
          radial-gradient(circle at 82% 12%, ${t1} 0%, transparent 36%),
          linear-gradient(180deg, ${t2} 0%, transparent 55%)
        `;
  let t4;
  if ($[6] !== t3) {
    t4 = <Box sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100vh",
      background: t3,
      pointerEvents: "none"
    }} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const HomeLandingHero: React.FC<HomeLandingHeroProps> = t0 => {
  const $ = _c(114);
  const {
    LL,
    pageScrollViewport
  } = t0;
  const theme = useTheme();
  const brandControlsRef = useRef(null);
  const {
    mobileEntranceSx
  } = useLandingDecor3dMobileEntrance("hero");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      controlsRef: brandControlsRef,
      baseAzimuth: LANDING_DECOR_BRAND_BASE_AZIMUTH,
      basePolar: LANDING_DECOR_BRAND_BASE_POLAR,
      invertPointerX: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useHeroOrbitModelMotion(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "relative",
      overflow: "visible",
      bgcolor: "background.default",
      pt: {
        xs: 14,
        md: 18
      },
      pb: {
        xs: 8,
        md: 10
      }
    };
    t3 = <AmbientBackground />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "absolute",
      top: {
        xs: -88,
        sm: -170,
        md: -280
      },
      right: {
        xs: -118,
        sm: -180,
        md: -320
      },
      width: LANDING_DECOR_CANVAS_SIZE_PX,
      height: LANDING_DECOR_CANVAS_SIZE_PX,
      opacity: LANDING_DECOR_GLASS_OPACITY_SX,
      filter: "blur(0.4px)",
      pointerEvents: "none",
      maskImage: "radial-gradient(circle at 48% 50%, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 32%, rgba(0,0,0,0.46) 66%, transparent 90%)"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== mobileEntranceSx) {
    t5 = [t4, mobileEntranceSx];
    $[4] = mobileEntranceSx;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LogoModelView controlsRef={brandControlsRef} interactive={false} cameraPosition={LANDING_DECOR_MODEL_CAMERA} cameraFov={LANDING_DECOR_MODEL_FOV} target={LANDING_DECOR_MODEL_TARGET} distanceRange={LANDING_DECOR_MODEL_DISTANCE} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <Box sx={t5}>{t6}</Box>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        lg: "minmax(0, 5fr) minmax(0, 7fr)"
      },
      gap: {
        xs: 4,
        md: 6
      },
      alignItems: "center"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      minWidth: 0
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      position: "relative",
      zIndex: 1
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      maxWidth: 700,
      fontSize: {
        xs: "2.15rem",
        sm: "3.4rem",
        md: "4.8rem"
      },
      lineHeight: {
        xs: 1.08,
        md: 1.02
      }
    };
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== LL) {
    t13 = LL.HOME_LANDING_TITLE();
    $[14] = LL;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13) {
    t14 = <Typography variant="h1" sx={t12}>{t13}</Typography>;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      color: "text.secondary",
      maxWidth: 620,
      fontSize: {
        xs: "1rem",
        md: "1.08rem"
      },
      lineHeight: 1.75
    };
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== LL) {
    t16 = LL.HOME_LANDING_SUBTITLE();
    $[19] = LL;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t16) {
    t17 = <Typography variant="body1" sx={t15}>{t16}</Typography>;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      xs: "column",
      sm: "row"
    };
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      pt: 1
    };
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== LL) {
    t21 = LL.TRY_IT_OUT_NOW();
    $[26] = LL;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== t21) {
    t22 = <Button component={Link} data-testid="cta-to-playground" href={LANDING_PRIMARY_PLAYGROUND_HREF} variant="contained" size="large" sx={t20}>{t21}</Button>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== LL) {
    t24 = LL.BROWSE_PROJECTS();
    $[31] = LL;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== t24) {
    t25 = <Button component={Link} href="/playground?view=browse" variant="outlined" size="large" sx={t23}>{t24}</Button>;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== t22 || $[36] !== t25) {
    t26 = <Stack direction={t18} spacing={1.5} sx={t19}>{t22}{t25}</Stack>;
    $[35] = t22;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      xs: "column",
      sm: "row"
    };
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      alignItems: {
        xs: "flex-start",
        sm: "center"
      }
    };
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== LL) {
    t29 = LL.HOME_HERO_FAQ_LINK();
    $[40] = LL;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== LL) {
    t30 = LL.HOME_HERO_FAQ_LINK_SUFFIX();
    $[42] = LL;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t29 || $[45] !== t30) {
    t31 = <Stack direction={t27} spacing={1} sx={t28}><Button component={Link} href="#faq" variant="text" color="inherit">{t29}{t30}</Button></Stack>;
    $[44] = t29;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      flexWrap: "wrap",
      pt: 1
    };
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== theme.appDesign.surfaceHigh) {
    t33 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[48] = theme.appDesign.surfaceHigh;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] !== theme.appDesign.outline) {
    t34 = alpha(theme.appDesign.outline, 0.14);
    $[50] = theme.appDesign.outline;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  const t35 = `1px solid ${t34}`;
  let t36;
  if ($[52] !== t33 || $[53] !== t35) {
    t36 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t33,
      border: t35
    };
    $[52] = t33;
    $[53] = t35;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  let t37;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      color: "text.secondary"
    };
    $[55] = t37;
  } else {
    t37 = $[55];
  }
  let t38;
  if ($[56] !== LL) {
    t38 = LL.HOME_LANG_JS_TITLE();
    $[56] = LL;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] !== t38) {
    t39 = <Typography variant="caption" sx={t37}>{t38}</Typography>;
    $[58] = t38;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] !== t36 || $[61] !== t39) {
    t40 = <Box sx={t36}>{t39}</Box>;
    $[60] = t36;
    $[61] = t39;
    $[62] = t40;
  } else {
    t40 = $[62];
  }
  let t41;
  if ($[63] !== theme.appDesign.surfaceHigh) {
    t41 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[63] = theme.appDesign.surfaceHigh;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  let t42;
  if ($[65] !== theme.appDesign.outline) {
    t42 = alpha(theme.appDesign.outline, 0.14);
    $[65] = theme.appDesign.outline;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  const t43 = `1px solid ${t42}`;
  let t44;
  if ($[67] !== t41 || $[68] !== t43) {
    t44 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t41,
      border: t43
    };
    $[67] = t41;
    $[68] = t43;
    $[69] = t44;
  } else {
    t44 = $[69];
  }
  let t45;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      color: "text.secondary"
    };
    $[70] = t45;
  } else {
    t45 = $[70];
  }
  let t46;
  if ($[71] !== LL) {
    t46 = LL.HOME_LANG_PYTHON_TITLE();
    $[71] = LL;
    $[72] = t46;
  } else {
    t46 = $[72];
  }
  let t47;
  if ($[73] !== t46) {
    t47 = <Typography variant="caption" sx={t45}>{t46}</Typography>;
    $[73] = t46;
    $[74] = t47;
  } else {
    t47 = $[74];
  }
  let t48;
  if ($[75] !== t44 || $[76] !== t47) {
    t48 = <Box sx={t44}>{t47}</Box>;
    $[75] = t44;
    $[76] = t47;
    $[77] = t48;
  } else {
    t48 = $[77];
  }
  let t49;
  if ($[78] !== theme.appDesign.surfaceHigh) {
    t49 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[78] = theme.appDesign.surfaceHigh;
    $[79] = t49;
  } else {
    t49 = $[79];
  }
  let t50;
  if ($[80] !== theme.appDesign.outline) {
    t50 = alpha(theme.appDesign.outline, 0.14);
    $[80] = theme.appDesign.outline;
    $[81] = t50;
  } else {
    t50 = $[81];
  }
  const t51 = `1px solid ${t50}`;
  let t52;
  if ($[82] !== t49 || $[83] !== t51) {
    t52 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t49,
      border: t51
    };
    $[82] = t49;
    $[83] = t51;
    $[84] = t52;
  } else {
    t52 = $[84];
  }
  let t53;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      color: "text.secondary"
    };
    $[85] = t53;
  } else {
    t53 = $[85];
  }
  let t54;
  if ($[86] !== LL) {
    t54 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[86] = LL;
    $[87] = t54;
  } else {
    t54 = $[87];
  }
  let t55;
  if ($[88] !== t54) {
    t55 = <Typography variant="caption" sx={t53}>{t54}</Typography>;
    $[88] = t54;
    $[89] = t55;
  } else {
    t55 = $[89];
  }
  let t56;
  if ($[90] !== t52 || $[91] !== t55) {
    t56 = <Box sx={t52}>{t55}</Box>;
    $[90] = t52;
    $[91] = t55;
    $[92] = t56;
  } else {
    t56 = $[92];
  }
  let t57;
  if ($[93] !== t40 || $[94] !== t48 || $[95] !== t56) {
    t57 = <Stack direction="row" spacing={1} useFlexGap={true} sx={t32}>{t40}{t48}{t56}</Stack>;
    $[93] = t40;
    $[94] = t48;
    $[95] = t56;
    $[96] = t57;
  } else {
    t57 = $[96];
  }
  let t58;
  if ($[97] !== t14 || $[98] !== t17 || $[99] !== t26 || $[100] !== t31 || $[101] !== t57) {
    t58 = <Box sx={t10}><Stack spacing={2.5} sx={t11}>{t14}{t17}{t26}{t31}{t57}</Stack></Box>;
    $[97] = t14;
    $[98] = t17;
    $[99] = t26;
    $[100] = t31;
    $[101] = t57;
    $[102] = t58;
  } else {
    t58 = $[102];
  }
  let t59;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = {
      minWidth: 0
    };
    $[103] = t59;
  } else {
    t59 = $[103];
  }
  let t60;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = {
      position: "relative",
      zIndex: 1,
      minWidth: 0,
      mx: {
        xs: "auto",
        lg: 0
      }
    };
    $[104] = t60;
  } else {
    t60 = $[104];
  }
  let t61;
  if ($[105] !== LL || $[106] !== pageScrollViewport) {
    t61 = <Box sx={t59}><Box sx={t60}><HomeLandingHeroPreview LL={LL} pageScrollViewport={pageScrollViewport} /></Box></Box>;
    $[105] = LL;
    $[106] = pageScrollViewport;
    $[107] = t61;
  } else {
    t61 = $[107];
  }
  let t62;
  if ($[108] !== t58 || $[109] !== t61) {
    t62 = <Container maxWidth="xl" sx={t8}><Box sx={t9}>{t58}{t61}</Box></Container>;
    $[108] = t58;
    $[109] = t61;
    $[110] = t62;
  } else {
    t62 = $[110];
  }
  let t63;
  if ($[111] !== t62 || $[112] !== t7) {
    t63 = <Box component="section" sx={t2}>{t3}{t7}{t62}</Box>;
    $[111] = t62;
    $[112] = t7;
    $[113] = t63;
  } else {
    t63 = $[113];
  }
  return t63;
};